/**
 * 품질관리 Console react-query 훅 모음
 * 내부 로직만 교체되었으므로 기존 탭 컴포넌트 인터페이스와 호환됩니다.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
    qaStandardApi,
    evaluationApi,
    reviewApi,
    policyApi,
    historyApi,
    settingsApi,
    healthApi,
} from '../../api/adminQualityApi';

// ─────────────────────────────────────────────
// 1. 표준 QA
// ─────────────────────────────────────────────
export function useStandardQA(params = {}) {
    return useQuery({
        queryKey: ['qa', 'list', params],
        queryFn: () => qaStandardApi.getList(params).then((res) => res.data),
        keepPreviousData: true,
    });
}

export function useStandardQADetail(id) {
    return useQuery({
        queryKey: ['qa', 'detail', id],
        queryFn: () => qaStandardApi.getOne(id).then((res) => res.data),
        enabled: !!id,
    });
}

export function useStandardQAMutations() {
    const qc = useQueryClient();
    const invalidate = () => qc.invalidateQueries(['qa', 'list']);

    const create = useMutation({
        mutationFn: qaStandardApi.create,
        onSuccess: () => { toast.success('저장되었습니다.'); invalidate(); },
    });

    const update = useMutation({
        mutationFn: ({ id, data }) => qaStandardApi.update(id, data),
        onSuccess: () => { toast.success('수정되었습니다.'); invalidate(); },
    });

    const remove = useMutation({
        mutationFn: qaStandardApi.remove,
        onSuccess: () => { toast.success('삭제되었습니다.'); invalidate(); },
    });

    return { create, update, remove };
}

// ─────────────────────────────────────────────
// 2. 평가 배치
// ─────────────────────────────────────────────
export function useBatches(params = {}) {
    return useQuery({
        queryKey: ['evaluation', 'batches', params],
        queryFn: () => evaluationApi.getBatches(params).then((res) => res.data),
        keepPreviousData: true,
    });
}

export function useRunEvaluation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: evaluationApi.run,
        onSuccess: () => {
            toast.success('평가가 시작되었습니다.');
            qc.invalidateQueries(['evaluation', 'batches']);
            qc.invalidateQueries(['evaluation', 'results']);
        },
    });
}

// ─────────────────────────────────────────────
// 3. 평가 결과
// ─────────────────────────────────────────────
export function useEvalResults(params = {}) {
    return useQuery({
        queryKey: ['evaluation', 'results', params],
        queryFn: () => evaluationApi.getResults(params).then((res) => res.data),
        keepPreviousData: true,
        enabled: Object.keys(params).length > 0 || true,
    });
}

export function useEvalResult(id) {
    return useQuery({
        queryKey: ['evaluation', 'result', id],
        queryFn: () => evaluationApi.getResult(id).then((res) => res.data),
        enabled: !!id,
    });
}

export function useEvalResultDetail(id) {
    return useQuery({
        queryKey: ['evaluation', 'result', 'detail', id],
        queryFn: () => evaluationApi.getResultDetail(id).then((res) => res.data),
        enabled: !!id,
    });
}

// ─────────────────────────────────────────────
// 4. 검토
// ─────────────────────────────────────────────
export function useReviews(params = {}) {
    return useQuery({
        queryKey: ['review', 'list', params],
        queryFn: () => reviewApi.getList(params).then((res) => res.data),
        keepPreviousData: true,
    });
}

export function useReviewDetail(id) {
    return useQuery({
        queryKey: ['review', 'detail', id],
        queryFn: () => reviewApi.getOne(id).then((res) => res.data),
        enabled: !!id,
    });
}

export function useReviewMutations() {
    const qc = useQueryClient();
    const invalidate = () => qc.invalidateQueries(['review', 'list']);

    const create = useMutation({
        mutationFn: reviewApi.create,
        onSuccess: () => { toast.success('검토 의견이 등록되었습니다.'); invalidate(); },
    });

    const update = useMutation({
        mutationFn: ({ id, data }) => reviewApi.update(id, data),
        onSuccess: () => { toast.success('수정되었습니다.'); invalidate(); },
    });

    const complete = useMutation({
        mutationFn: ({ id, data }) => reviewApi.complete(id, data),
        onSuccess: () => { toast.success('검토가 완료 처리되었습니다.'); invalidate(); },
    });

    return { create, update, complete };
}

// ─────────────────────────────────────────────
// 5. 정책
// ─────────────────────────────────────────────
export function usePolicies(params = {}) {
    return useQuery({
        queryKey: ['policy', 'list', params],
        queryFn: () => policyApi.getList(params).then((res) => res.data),
        keepPreviousData: true,
    });
}

export function usePolicyDetail(id) {
    return useQuery({
        queryKey: ['policy', 'detail', id],
        queryFn: () => policyApi.getOne(id).then((res) => res.data),
        enabled: !!id,
    });
}

export function usePolicyMutations() {
    const qc = useQueryClient();
    const invalidate = () => qc.invalidateQueries(['policy', 'list']);

    const create = useMutation({
        mutationFn: policyApi.create,
        onSuccess: () => { toast.success('정책이 등록되었습니다.'); invalidate(); },
    });

    const update = useMutation({
        mutationFn: ({ id, data }) => policyApi.update(id, data),
        onSuccess: () => { toast.success('수정되었습니다.'); invalidate(); },
    });

    const remove = useMutation({
        mutationFn: policyApi.remove,
        onSuccess: () => { toast.success('정책이 삭제되었습니다.'); invalidate(); },
    });

    const activate = useMutation({
        mutationFn: policyApi.activate,
        onSuccess: () => { toast.success('정책이 활성화되었습니다.'); invalidate(); },
    });

    const deactivate = useMutation({
        mutationFn: policyApi.deactivate,
        onSuccess: () => { toast.success('정책이 비활성화되었습니다.'); invalidate(); },
    });

    const apply = useMutation({
        mutationFn: policyApi.apply,
        onSuccess: (res) => {
            toast.success(`정책 ${res?.data?.applied_count ?? ''}건이 엔진에 반영되었습니다.`);
            invalidate();
            qc.invalidateQueries(['policy', 'apply-history']); // 반영 이력 패널 자동 갱신
        },
    });

    return { create, update, remove, activate, deactivate, apply };
}

export function usePolicyApplyHistory(params = {}) {
    return useQuery({
        queryKey: ['policy', 'apply-history', params],
        queryFn: () => policyApi.getApplyHistory(params).then((res) => res.data),
        keepPreviousData: true,
    });
}

// ─────────────────────────────────────────────
// 6. 이력
// ─────────────────────────────────────────────
export function useHistory(params = {}) {
    return useQuery({
        queryKey: ['history', 'list', params],
        queryFn: () => historyApi.getList(params).then((res) => res.data),
        keepPreviousData: true,
    });
}

export function useHistoryMutations() {
    const qc = useQueryClient();
    const invalidate = () => qc.invalidateQueries(['history', 'list']);

    const regenerate = useMutation({
        mutationFn: historyApi.regenerate,
        onSuccess: () => { toast.success('재생성 요청이 등록되었습니다.'); invalidate(); },
    });

    const approve = useMutation({
        mutationFn: historyApi.approve,
        onSuccess: () => { toast.success('승인되었습니다.'); invalidate(); },
    });

    const reject = useMutation({
        mutationFn: historyApi.reject,
        onSuccess: () => { toast.success('반려 처리되었습니다.'); invalidate(); },
    });

    return { regenerate, approve, reject };
}

// ─────────────────────────────────────────────
// 7. 시스템 설정
// ─────────────────────────────────────────────
export function useSettings(params = {}) {
    return useQuery({
        queryKey: ['settings', params],
        queryFn: () => settingsApi.getAll(params).then((res) => res.data),
    });
}

// ─────────────────────────────────────────────
// 8. 시스템 상태
// ─────────────────────────────────────────────
export function useHealth() {
    return useQuery({
        queryKey: ['health'],
        queryFn: () => healthApi.check().then((res) => res.data),
        refetchInterval: 30000, // 30초마다 자동 갱신
    });
}

export function useSettingsSchema() {
    return useQuery({
        queryKey: ['settings', 'schema'],
        queryFn: () => settingsApi.getSchema().then((res) => res.data),
        staleTime: Infinity,
    });
}

export function useSettingsMutations() {
    const qc = useQueryClient();

    const update = useMutation({
        mutationFn: settingsApi.update,
        onSuccess: () => { toast.success('설정이 저장되었습니다.'); qc.invalidateQueries(['settings']); },
    });

    const reload = useMutation({
        mutationFn: settingsApi.reload,
        onSuccess: (res) => {
            toast.success(`엔진 설정이 재로드되었습니다. (${res?.data?.engine_version ?? ''})`);
        },
    });

    return { update, reload };
}
